import { ChangeDetectionStrategy, Component, Inject } from "@angular/core";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { ADAPT_DIALOG_DATA } from "@common/ux/adapt-common-dialog/adapt-common-dialog.globals";
import { BaseDialogComponent } from "@common/ux/adapt-common-dialog/base-dialog.component/base-dialog.component";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { forkJoin, tap } from "rxjs";
import { VisionService } from "../vision.service";
import { VisionAuthService } from "../vision-auth.service";

@Component({
    selector: "adapt-capture-vision-dialog",
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: "./capture-vision-dialog.component.html",
})
export class CaptureVisionDialogComponent extends BaseDialogComponent<number> {
    public readonly dialogName = "CaptureVisionDialog";
    public hasExistingVision = false;
    public captureInProgress = false;

    public teamMembers: Person[] = [];
    public participatingMemberIds: number[] = [];

    public constructor(
        @Inject(ADAPT_DIALOG_DATA) public teamId: number,
        private visionService: VisionService,
        private visionAuthService: VisionAuthService,
        teamsService: CommonTeamsService,
    ) {
        super();

        forkJoin([
            teamsService.getActiveTeamMembers(teamId),
            this.visionService.getVisionsByTeam(teamId),
        ]).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(([members, visions]) => {
            this.teamMembers = members.sort(SortUtilities.getSortByFieldFunction("fullName"));
            this.hasExistingVision = visions.length > 0;
            this.captureInProgress = !this.visionService.areAllVisionsCompleted(visions);
            this.isInitialised = true;
        });
    }

    public isParticipating(personId: number) {
        return this.participatingMemberIds.includes(personId);
    }

    public personCanEditVision(person: Person) {
        return this.visionAuthService.personCanEditVisionForTeamId(person, this.teamId);
    }

    public onParticipationChange(personId: number, participating: boolean) {
        if (participating) {
            if (!this.isParticipating(personId)) {
                this.participatingMemberIds.push(personId);
            }
        } else {
            this.participatingMemberIds = this.participatingMemberIds.filter((id) => id !== personId);
        }
    }

    public toggleParticipation(personId: number) {
        const currentlyParticipating = this.isParticipating(personId);
        this.onParticipationChange(personId, !currentlyParticipating);
    }

    @Autobind
    public captureVision() {
        return this.visionService.captureVisionForTeam(this.teamId, this.participatingMemberIds).pipe(
            tap(() => this.resolve(this.teamId)),
        );
    }
}
